import React, { useState, useEffect, useMemo, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';
import Badge from '../../../components/ui/Badge';
import { formatCurrency } from '../../../utils/formatCurrency';
import { statusVariant } from '../../../utils/statusVariant';
import { listLinkableExtras, linkExistingExtra } from '../../../services/projectDirectivesService';

/**
 * Relier une directive a un extra / credit EXISTANT du projet.
 *
 * Ne propose que les extras libres : sans directive_id, et qu'aucune directive
 * ne pointe deja par extra_id (listLinkableExtras). Le lien est pose par
 * linkExistingExtra, qui reserve l'extra avant de toucher la directive.
 *
 * La modale reste montee entre deux ouvertures : son etat (liste, recherche,
 * selection) est donc rattache a la directive pour laquelle la liste a ete lue
 * (`forId`). Tant que la liste de CETTE ouverture n'est pas arrivee, aucune
 * ligne n'est rendue ni cliquable ; la selection (un id, jamais un index)
 * n'est valable que si cet id figure dans la liste affichee.
 */

// Libelle d'un extra : n° et titre (toast, rappel de la selection).
const labelOf = (extra) => [extra?.co_number, extra?.title]?.filter(Boolean)?.join(' · ') || '—';

// Montant affiche : accepte une fois approuve, sinon soumis (cout + profit,
// comme le tableau des extras). Un credit s'affiche en negatif.
const amountOf = (extra) => {
  const accepted = extra?.status === 'approved' && extra?.cost_accepted != null ? parseFloat(extra?.cost_accepted) || 0 : null;
  const value = accepted ?? ((parseFloat(extra?.cost_submitted ?? extra?.amount) || 0) + (parseFloat(extra?.profit) || 0));
  return extra?.type === 'credit' ? -Math.abs(value) : value;
};

const LinkExtraModal = ({ directive, projectId, onClose, onLinked }) => {
  const { t } = useTranslation();
  // forId : directive pour laquelle `rows` a ete lue (null = rien de valable).
  const [state, setState] = useState({ forId: null, loading: true, error: false, rows: [] });
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Numero de la derniere lecture : une reponse plus ancienne est ignoree.
  const requestRef = useRef(0);

  useEscapeKey(onClose, !!directive && !saving);

  // Lit la liste des extras libres pour `forId` ; seule la lecture la plus
  // recente est appliquee (fermeture, autre directive, relecture apres erreur).
  const loadRows = (forId) => {
    const requestId = ++requestRef.current;
    setState({ forId, loading: true, error: false, rows: [] });
    listLinkableExtras(projectId)
      ?.then((rows) => {
        if (requestId === requestRef.current) setState({ forId, loading: false, error: false, rows: rows || [] });
      })
      ?.catch(() => {
        if (requestId === requestRef.current) setState({ forId, loading: false, error: true, rows: [] });
      });
  };

  // Chaque ouverture repart de zero : liste, recherche, selection, erreur.
  useEffect(() => {
    requestRef.current += 1; // toute lecture en vol devient perimee
    setSelectedId(null);
    setQuery('');
    setSaveError(null);
    if (!directive?.id) {
      setState({ forId: null, loading: true, error: false, rows: [] });
      return;
    }
    loadRows(directive?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directive?.id, projectId]);

  // Liste de CETTE ouverture seulement : avant son arrivee, rien n'est rendu.
  const ready = !!directive?.id && state?.forId === directive?.id && !state?.loading;
  const rows = ready ? state?.rows : [];

  const filtered = useMemo(() => {
    const q = query?.trim()?.toLowerCase();
    if (!q) return rows;
    return rows?.filter((e) =>
      [e?.co_number, e?.title]?.some((v) => (v || '')?.toLowerCase()?.includes(q))
    );
  }, [rows, query]);

  // La selection n'est valable que si son id est dans la liste affichee.
  const selected = useMemo(
    () => (selectedId ? filtered?.find((e) => e?.id === selectedId) || null : null),
    [filtered, selectedId]
  );

  if (!directive) return null;

  const handleLink = async () => {
    if (!selected || saving) return;
    const chosen = selected; // l'extra reellement envoye, fige au clic
    setSaving(true);
    setSaveError(null);
    try {
      const result = await linkExistingExtra({ directiveId: directive?.id, extraId: chosen?.id });
      if (result?.extra?.id && result?.extra?.id !== chosen?.id) {
        console.error('[directives] lien : extra renvoye different de l\'extra choisi', result?.extra?.id, chosen?.id);
      }
      await onLinked?.({ ...chosen, co_number: result?.extra?.co_number ?? chosen?.co_number });
      onClose?.();
    } catch (err) {
      setSaveError(
        err?.code === 'EXTRA_ALREADY_LINKED'
          ? t('extras.directives.link.alreadyLinked')
          : err?.message === 'EXTRA_DETACH_FAILED'
            ? t('extras.directives.detachError', { number: directive?.extra?.co_number || '—' })
            : t('extras.directives.link.error')
      );
      // L'extra a pu etre pris entre-temps : la liste est relue (lecture gardee)
      // et la selection invalidee.
      if (err?.code === 'EXTRA_ALREADY_LINKED') {
        setSelectedId(null);
        loadRows(directive?.id);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* En-tete */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
              <Icon name="Link" size={18} className="text-orange-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 leading-snug truncate">{t('extras.directives.link.title')}</h3>
              <p className="text-[11px] text-gray-500 truncate">
                {directive?.nomenclature || '—'} · {t('extras.directives.link.subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Recherche */}
        <div className="px-5 pt-3 flex-shrink-0">
          <div className="relative">
            <Icon name="Search" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e?.target?.value)}
              placeholder={t('extras.directives.link.search')}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!ready ? (
            <div className="flex justify-center py-8">
              <Icon name="Loader2" size={20} className="animate-spin text-primary" />
            </div>
          ) : state?.error ? (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{t('extras.directives.link.loadError')}</p>
          ) : rows?.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">{t('extras.directives.link.empty')}</p>
          ) : filtered?.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">{t('extras.directives.link.noMatch')}</p>
          ) : (
            <ul className="space-y-1" role="listbox">
              {filtered?.map((e) => {
                const isSelected = e?.id === selected?.id;
                return (
                  <li key={e?.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={saving}
                      onClick={() => setSelectedId(e?.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors disabled:cursor-not-allowed ${
                        isSelected ? 'border-primary bg-orange-50' : 'border-transparent hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 w-3.5 h-3.5 rounded-full border ${isSelected ? 'border-primary bg-primary' : 'border-gray-300 bg-white'}`}
                        aria-hidden="true"
                      />
                      <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        e?.type === 'credit' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {t(`extras.types.${e?.type || 'extra'}`)}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-medium text-gray-900 truncate">
                          {e?.co_number || '—'}{e?.title ? ` · ${e?.title}` : ''}
                        </span>
                      </span>
                      <span className="flex-shrink-0 text-xs text-gray-700 tabular-nums">{formatCurrency(amountOf(e))}</span>
                      {e?.status && (
                        <Badge variant={statusVariant('extras', e?.status)}>{t(`status.${e?.status}`)}</Badge>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pied */}
        <div className="px-5 py-3 border-t border-gray-100 flex-shrink-0 space-y-2">
          {saveError && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveError}</p>
          )}
          <div className="flex items-center justify-end gap-2">
            {/* Rappel de l'extra qui sera reellement relie */}
            <p className="flex-1 min-w-0 text-[11px] text-gray-500 truncate">
              {selected ? t('extras.directives.link.selected', { label: labelOf(selected) }) : ''}
            </p>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-60"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleLink}
              disabled={!selected || saving}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
            >
              {saving ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Link" size={13} />}
              {saving ? t('extras.directives.link.linking') : t('extras.directives.link.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkExtraModal;
